import React, { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { Badge } from "./ui/badge";
import { API } from "../App";

/**
 * DynamicFormRenderer - Renders form fields based on Form Builder configuration
 * 
 * Props:
 * - formType: "lead_capture" | "lead_internal" | "eoi" | "site_visit"
 * - projectId: Optional project ID for project-specific configs
 * - source: Optional source for source-specific configs
 * - values: Object containing current field values
 * - onChange: Function called when a field value changes (fieldName, value)
 * - errors: Object containing field validation errors
 * - excludeFields: Array of field names to exclude from rendering
 * - additionalFields: Array of custom field configs to add (for source-specific fields like referrer)
 * - projects: Array of projects for project dropdown
 * - disabled: Boolean to disable all fields
 * - gridCols: Number of columns for grid layout (default: 2)
 * - showGroups: Boolean to show field group headers (default: true)
 * - theme: "light" | "dark" - UI theme (default: "light")
 */
const DynamicFormRenderer = ({
  formType = "lead_internal",
  projectId = null,
  source = null,
  values = {},
  onChange,
  errors = {},
  excludeFields = [],
  additionalFields = [],
  projects = [],
  disabled = false,
  gridCols = 2,
  showGroups = true,
  theme = "light",
}) => {
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Theme classes
  const isDark = theme === "dark";
  const labelClass = isDark ? "text-xs text-slate-400 mb-1 block" : "text-sm font-medium text-slate-700";
  const inputClass = isDark ? "bg-slate-900/60 border-slate-600 text-white placeholder:text-slate-500" : "";
  const selectClass = isDark ? "bg-slate-900/60 border-slate-600 text-white" : "";
  const groupHeaderClass = isDark ? "text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" : "text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3";

  useEffect(() => {
    fetchFormConfig();
  }, [formType, projectId, source]);

  const fetchFormConfig = async () => {
    try {
      let url = `${API}/form-config/active/${formType}`;
      const params = [];
      if (projectId) params.push(`project_id=${projectId}`);
      if (source) params.push(`source=${source}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const res = await axios.get(url);
      setFormConfig(res.data);
    } catch (e) {
      console.error("Failed to load form config:", e);
      setFormConfig(null);
    } finally {
      setLoading(false);
    }
  };

  // Evaluate conditional logic
  const shouldShowField = (field) => {
    if (!field.conditional) return true;
    
    const { depends_on, operator, value } = field.conditional;
    const dependentValue = values[depends_on] || values[getFieldNameById(depends_on)];
    
    switch (operator) {
      case "equals":
        return dependentValue === value || String(dependentValue) === String(value);
      case "not_equals":
        return dependentValue !== value && String(dependentValue) !== String(value);
      case "contains":
        return String(dependentValue || "").toLowerCase().includes(String(value).toLowerCase());
      case "in":
        return Array.isArray(value) ? value.includes(dependentValue) : false;
      default:
        return true;
    }
  };

  // Get field name by field_id (for conditional logic)
  const getFieldNameById = (fieldId) => {
    const field = formConfig?.fields?.find(f => f.field_id === fieldId);
    return field?.name || fieldId;
  };

  // Handle field value change
  const handleFieldChange = (fieldName, value) => {
    if (onChange) {
      onChange(fieldName, value);
    }
  };

  // Render a single field based on its type
  const renderField = (field) => {
    const { field_id, name, label, field_type, placeholder, help_text, required, options, auto_capture } = field;
    const value = values[name] ?? "";
    const error = errors[name];
    const isDisabled = disabled || auto_capture;

    // Skip hidden fields in UI (they're auto-captured)
    if (field_type === "hidden") {
      return null;
    }

    const commonProps = {
      id: field_id,
      name: name,
      disabled: isDisabled,
      className: `${error ? "border-red-500" : ""} ${inputClass}`.trim(),
      "data-testid": `field-${name}`,
    };

    let fieldElement;

    switch (field_type) {
      case "text":
      case "phone":
      case "email":
        fieldElement = (
          <Input
            {...commonProps}
            type={field_type === "email" ? "email" : "text"}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
          />
        );
        break;

      case "number":
        fieldElement = (
          <Input
            {...commonProps}
            type="number"
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value ? parseFloat(e.target.value) : "")}
          />
        );
        break;

      case "textarea":
        fieldElement = (
          <Textarea
            {...commonProps}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
            rows={3}
          />
        );
        break;

      case "dropdown":
        // Special handling for project_interest field
        if (name === "project_interest" && projects.length > 0) {
          fieldElement = (
            <Select
              value={value}
              onValueChange={(v) => handleFieldChange(name, v)}
              disabled={isDisabled}
            >
              <SelectTrigger {...commonProps}>
                <SelectValue placeholder={placeholder || "Select..."} />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                ))}
                <SelectItem value="undecided">Undecided</SelectItem>
              </SelectContent>
            </Select>
          );
        } else if (name === "project" && projects.length > 0) {
          // For site visit / revisit project dropdown (uses project name)
          fieldElement = (
            <Select
              value={value}
              onValueChange={(v) => handleFieldChange(name, v)}
              disabled={isDisabled}
            >
              <SelectTrigger {...commonProps}>
                <SelectValue placeholder={placeholder || "Select project..."} />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        } else if (name === "project_id" && projects.length > 0) {
          // For EOI project dropdown (uses project_id)
          fieldElement = (
            <Select
              value={value}
              onValueChange={(v) => handleFieldChange(name, v)}
              disabled={isDisabled}
            >
              <SelectTrigger {...commonProps}>
                <SelectValue placeholder={placeholder || "Select project..."} />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        } else {
          fieldElement = (
            <Select
              value={value}
              onValueChange={(v) => handleFieldChange(name, v)}
              disabled={isDisabled}
            >
              <SelectTrigger {...commonProps}>
                <SelectValue placeholder={placeholder || "Select..."} />
              </SelectTrigger>
              <SelectContent>
                {(options || []).map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        
        // Check if selected option triggers custom input
        const selectedOption = (options || []).find(o => o.value === value);
        if (selectedOption?.triggers_custom) {
          const customFieldName = `${name}_other`;
          fieldElement = (
            <div className="space-y-2">
              {fieldElement}
              <Input
                placeholder={`Specify ${label}...`}
                value={values[customFieldName] || ""}
                onChange={(e) => handleFieldChange(customFieldName, e.target.value)}
                disabled={isDisabled}
                className={inputClass}
                data-testid={`field-${customFieldName}`}
              />
            </div>
          );
        }
        break;

      case "multi_select":
        const selectedValues = Array.isArray(value) ? value : [];
        fieldElement = (
          <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[40px]">
            {(options || []).map(opt => (
              <Badge
                key={opt.value}
                variant={selectedValues.includes(opt.value) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => {
                  if (isDisabled) return;
                  const newValues = selectedValues.includes(opt.value)
                    ? selectedValues.filter(v => v !== opt.value)
                    : [...selectedValues, opt.value];
                  handleFieldChange(name, newValues);
                }}
              >
                {opt.label}
              </Badge>
            ))}
          </div>
        );
        break;

      case "date":
        fieldElement = (
          <Input
            {...commonProps}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
          />
        );
        break;

      case "datetime":
        fieldElement = (
          <Input
            {...commonProps}
            type="datetime-local"
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
          />
        );
        break;

      case "boolean":
        fieldElement = (
          <div className="flex items-center gap-3 pt-2">
            <Switch
              checked={!!value}
              onCheckedChange={(v) => handleFieldChange(name, v)}
              disabled={isDisabled}
              data-testid={`field-${name}`}
            />
            <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{value ? "Yes" : "No"}</span>
          </div>
        );
        break;

      case "radio":
        fieldElement = (
          <div className="flex flex-wrap gap-3 pt-2">
            {(options || []).map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleFieldChange(name, e.target.value)}
                  disabled={isDisabled}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        );
        break;

      default:
        fieldElement = (
          <Input
            {...commonProps}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
          />
        );
    }

    return (
      <div key={field_id} className="space-y-1.5">
        <label htmlFor={field_id} className={labelClass}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {fieldElement}
        {help_text && (
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{help_text}</p>
        )}
        {error && (
          <p className="text-xs text-red-500">{error}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-slate-500">
        Loading form...
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div className="py-8 text-center text-slate-500">
        Form configuration not available
      </div>
    );
  }

  // Filter and prepare fields
  let fields = (formConfig.fields || [])
    .filter(f => !excludeFields.includes(f.name) && !excludeFields.includes(f.field_id))
    .filter(f => f.visible !== false)
    .filter(shouldShowField);

  // Add additional fields
  if (additionalFields.length > 0) {
    fields = [...fields, ...additionalFields];
  }

  // Sort by order
  fields.sort((a, b) => (a.order || 999) - (b.order || 999));

  // Group fields if showGroups is true
  if (showGroups) {
    const groupedFields = fields.reduce((acc, field) => {
      const group = field.group || "other";
      if (!acc[group]) acc[group] = [];
      acc[group].push(field);
      return acc;
    }, {});

    const groupOrder = ["contact", "details", "visit", "observation", "payment", "preferences", "profile", "timeline", "other", "utm"];
    const groupLabels = {
      contact: "Contact Information",
      details: "Details",
      visit: "Visit Details",
      observation: "Observations",
      payment: "Payment Information",
      preferences: "Preferences",
      profile: "Buyer Profile",
      timeline: "Timeline",
      other: "Additional Information",
      utm: "Tracking",
    };

    return (
      <div className="space-y-6">
        {groupOrder.map(groupKey => {
          const groupFields = groupedFields[groupKey];
          if (!groupFields || groupFields.length === 0) return null;
          
          // Skip UTM group as those are hidden
          if (groupKey === "utm") return null;

          return (
            <div key={groupKey}>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {groupLabels[groupKey] || groupKey}
              </h4>
              <div className={`grid grid-cols-${gridCols} gap-4`}>
                {groupFields.map(field => renderField(field))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Simple grid layout without groups
  return (
    <div className={`grid grid-cols-${gridCols} gap-4`}>
      {fields.map(field => renderField(field))}
    </div>
  );
};

// Hook to get UTM values from URL
export const useUTMParams = () => {
  const [utmParams, setUtmParams] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_campaign: params.get("utm_campaign") || "",
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_term: params.get("utm_term") || "",
    };
    setUtmParams(utm);
  }, []);

  return utmParams;
};

// Validation helper
export const validateFormFields = (fields, values) => {
  const errors = {};
  
  fields.forEach(field => {
    if (field.required && field.visible !== false) {
      const value = values[field.name];
      if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
        errors[field.name] = `${field.label} is required`;
      }
    }
    
    // Validate email format
    if (field.field_type === "email" && values[field.name]) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(values[field.name])) {
        errors[field.name] = "Invalid email format";
      }
    }
    
    // Validate phone format
    if (field.field_type === "phone" && values[field.name]) {
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(values[field.name].replace(/[\s\-\+]/g, ""))) {
        errors[field.name] = "Invalid phone number";
      }
    }
    
    // Custom validation regex
    if (field.validation_regex && values[field.name]) {
      const regex = new RegExp(field.validation_regex);
      if (!regex.test(values[field.name])) {
        errors[field.name] = field.validation_message || "Invalid format";
      }
    }
  });
  
  return errors;
};

export default DynamicFormRenderer;
